/**
 * The theme rule, written once for both ends.
 *
 * There are three parties to a theme — the row in `themes`, the block in
 * `src/theme.css`, and the `data-theme` attribute on `<html>` — and exactly
 * one question worth getting right between them: *which theme does this
 * person actually get?* That question is answered here, and `server/themes.ts`
 * and `src/theme.ts` both import the answer rather than each writing it, so
 * the server and the browser cannot disagree about what somebody is looking
 * at.
 *
 * **Nothing in this module knows which themes are premium.** It is handed the
 * catalogue — the rows — and reads the flag off them. That is the whole point
 * of `themes.is_premium` being a column: making Terminal free next month is an
 * `UPDATE`, applied to everyone on their next request, with no deploy and no
 * list of ids in a constant anywhere. See migration 0017.
 */

/** What one row of `themes` is, as far as either end needs to know. */
export type ThemeChoice = {
  id: string
  name: string
  /** Gated on the `premium_themes` limit. A column, never a hard-coded list. */
  premium: boolean
  /** Exactly one row carries this; the partial unique index makes it true. */
  isDefault: boolean
}

/**
 * Where a theme goes when there is no catalogue to consult at all: the inline
 * script in `index.html` has no session and no database, and a half-migrated
 * environment has no rows. It is Midnight because `src/theme.css` declares
 * Midnight on bare `:root`, so this constant and the stylesheet degrade to the
 * same place rather than to two different ones.
 */
export const DEFAULT_THEME_ID = 'midnight'

/** The attribute the stylesheet keys off, on `<html>`. */
export const THEME_ATTRIBUTE = 'data-theme'

/**
 * Where the browser remembers it, so the *next* first paint is right before
 * any request has been made. The inline script in `index.html` reads this key
 * by its literal text; `tests/theme-client.test.ts` checks the two agree.
 */
export const THEME_STORAGE_KEY = 'friendloan.theme'

/** The cross-fade, in milliseconds. `--fade` in `src/theme.css` matches. */
export const THEME_FADE_MS = 180

/**
 * A `data-theme` value we are willing to put in the DOM.
 *
 * Deliberately a shape check and not a membership check: a theme added to the
 * database is meant to reach a browser running yesterday's bundle, and the
 * stylesheet already answers an id it does not have by leaving Midnight in
 * place. What this refuses is the other thing — a value out of `localStorage`,
 * which anything on the origin can write, being pasted into an attribute
 * selector's worth of the DOM.
 */
export const THEME_ID_PATTERN = /^[a-z][a-z0-9-]{0,31}$/

export function isThemeId(value: unknown): value is string {
  return typeof value === 'string' && THEME_ID_PATTERN.test(value)
}

/** The row marked default, or Midnight when there is no catalogue to ask. */
export function defaultThemeId(catalogue: readonly ThemeChoice[]): string {
  return catalogue.find((theme) => theme.isDefault)?.id ?? DEFAULT_THEME_ID
}

/** Whether this account may choose `theme` — the only place the gate is read. */
export function canChoose(theme: ThemeChoice, premiumAllowed: boolean): boolean {
  return premiumAllowed || !theme.premium
}

/**
 * **The lapsed-Premium path, and the reason this module exists.**
 *
 * Somebody picked Sunset while they were on Plus. Their subscription ends. On
 * the next request `premium_themes` resolves to 0, and this returns the
 * default theme instead — Midnight. What it does *not* do is throw, refuse, or
 * hand back an id with no stylesheet behind it, because every one of those
 * ends with a blank screen for somebody whose card expired, which is a
 * terrible way to be told about it.
 *
 * Their choice is not destroyed either. `users.theme_id` still says `sunset`,
 * this only decides what is drawn today, and the day they resubscribe Sunset
 * comes back on its own with nothing to re-pick. That is why the caller gets
 * both ids back from `resolveTheme` and the screen can say "Sunset is part of
 * Premium" beside a Midnight page.
 *
 * The same three answers cover the two other ways a choice goes stale: an id
 * that is not in the catalogue (a row deleted, or a client ahead of a deploy),
 * and no choice at all (a new account, `theme_id` NULL).
 */
export function effectiveThemeId(
  chosenId: string | null,
  catalogue: readonly ThemeChoice[],
  premiumAllowed: boolean,
): string {
  const fallback = defaultThemeId(catalogue)
  if (chosenId === null) return fallback
  const chosen = catalogue.find((theme) => theme.id === chosenId)
  if (!chosen) return fallback
  return canChoose(chosen, premiumAllowed) ? chosen.id : fallback
}

/** What the effective theme is, and whether it is the one that was asked for. */
export type ResolvedTheme = {
  /** What to draw. Always a theme in the catalogue (or Midnight). */
  id: string
  /** What is on the user row. `null` means "whatever the default is". */
  chosenId: string | null
  /** True when `chosenId` names a theme this account may not use today. */
  locked: boolean
}

export function resolveThemeChoice(
  chosenId: string | null,
  catalogue: readonly ThemeChoice[],
  premiumAllowed: boolean,
): ResolvedTheme {
  const id = effectiveThemeId(chosenId, catalogue, premiumAllowed)
  const chosen = chosenId === null ? undefined : catalogue.find((theme) => theme.id === chosenId)
  return { id, chosenId, locked: chosen !== undefined && !canChoose(chosen, premiumAllowed) }
}
